import React, { useCallback, useEffect, useState } from "react";
import { useField, useForm } from "react-final-form";
import { CheckCircle, CheckCircleOutline } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";

import { Table, TableBody, TableCell, TableHead, TableRow } from "ui";

import { loadJson } from "fond/fileValidation";
import { Dropzone } from "fond/widgets";
import { validFileName } from "fond/widgets/Dropzone/utils";

import { c2sFileMap } from "./files";
import { C2SLayersConfiguration, resolveLayerConfigurationInputLayers } from "./helper";
import { FormData } from "./SetupForm";

const FILE_ERROR_ERROR_MESSAGE = "Invalid file, please try another file.";

export interface C2SUploadArtifacts {
  costBreakdown: string;
  bom: string;
  layersConfiguration: C2SLayersConfiguration;
}

const UploadConfigurationStep: React.FC = () => {
  const { change } = useForm();
  const {
    input: { value: files },
  } = useField<File[]>("Files.Artifacts");

  const {
    input: { value: inputs },
    meta: { error: stepError, touched },
  } = useField<FormData["Inputs"]>("Inputs", {
    validate: (value) => {
      if (!value?.CostBreakdown.Data || !value?.BillOfMaterials.Data || !value?.ToolLayersConfiguration.LayersConfiguration.Data) {
        return "All input files must be provided";
      }
      return undefined;
    },
  });

  const [fileErrors, setFileErrors] = useState<Partial<Record<keyof C2SUploadArtifacts, string>>>({});

  // Process files when all are uploaded
  useEffect(() => {
    const processFiles = async () => {
      const layersConfigFile = files.find((file) => file.name === "layers_configuration.json");
      if (layersConfigFile) {
        try {
          const content = await loadJson(layersConfigFile);
          resolveLayerConfigurationInputLayers(content);
          change("Inputs.ToolLayersConfiguration.LayersConfiguration.Data", content);
          setFileErrors(({ layersConfiguration, ...prev }) => prev);
        } catch (e) {
          change("Inputs.ToolLayersConfiguration.LayersConfiguration.Data", "");
          setFileErrors((prev) => ({ ...prev, layersConfiguration: FILE_ERROR_ERROR_MESSAGE }));
        }
      }

      const costBreakdownFile = files.find((file) => file.name === "cost_breakdown.csv");
      if (costBreakdownFile) {
        try {
          const value = await costBreakdownFile.text();
          change("Inputs.CostBreakdown.Data", value);
          setFileErrors(({ costBreakdown, ...prev }) => prev);
        } catch (e) {
          change("Inputs.CostBreakdown.Data", null);
          setFileErrors((prev) => ({ ...prev, costBreakdown: FILE_ERROR_ERROR_MESSAGE }));
        }
      }

      const bomFile = files.find((file) => file.name === "bom.csv");
      if (bomFile) {
        try {
          const value = await bomFile.text();
          change("Inputs.BillOfMaterials.Data", value);
          setFileErrors(({ bom, ...prev }) => prev);
        } catch (e) {
          change("Inputs.BillOfMaterials.Data", null);
          setFileErrors((prev) => ({ ...prev, bom: FILE_ERROR_ERROR_MESSAGE }));
        }
      }
    };

    processFiles();
  }, [change, files]);

  const handleFilesChange = useCallback(
    async (newFiles: File[]) => {
      // Create a map of existing files by name for quick lookup
      const existingFileMap = new Map(files.map((file) => [file.name, file]));

      // Add new files, replacing any existing files with the same name
      newFiles.forEach((newFile) => {
        existingFileMap.set(newFile.name, newFile);
      });

      // Also store the files within the form state.
      change("Files.Artifacts", Array.from(existingFileMap.values()));
    },
    [change, files]
  );

  const renderFileStatus = useCallback(() => {
    return c2sFileMap.ArtifactFiles.map(({ id, file, title }) => {
      let success = false;
      if (id === "costBreakdown") {
        success = !!inputs?.CostBreakdown?.Data;
      } else if (id === "bom") {
        success = !!inputs?.BillOfMaterials?.Data;
      } else if (id === "layersConfiguration") {
        success = !!inputs?.ToolLayersConfiguration?.LayersConfiguration?.Data;
      }

      const hasError = fileErrors[id];
      const IconComponent = success && !hasError ? CheckCircle : CheckCircleOutline;
      const color = hasError ? "error" : success ? "success" : "action";
      return (
        <TableRow key={title}>
          <TableCell>
            <Box display="flex" alignItems="center">
              <IconComponent color={color} sx={{ mr: 1 }} />
              <Typography sx={{ fontWeight: 500 }} data-testid="file-title">
                {title}
              </Typography>
              <Typography variant="body3" sx={{ ml: 1 }} data-testid="file-extensions">
                ({file})
              </Typography>
              {hasError && (
                <Typography variant="body3" sx={{ ml: 2 }} color="error.main" data-testid="file-status">
                  {fileErrors[id]}
                </Typography>
              )}
            </Box>
          </TableCell>
        </TableRow>
      );
    });
  }, [fileErrors, inputs]);

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Artifacts
        </Typography>
        <Dropzone
          acceptedFiles={{
            "application/json": [".json"],
            "text/csv": [".csv"],
          }}
          message="Drag and drop your artifact files or click to browse."
          validator={validFileName(c2sFileMap.ArtifactFiles.map(({ file }) => file))}
          onDropAccepted={handleFilesChange}
          data-testid="artifacts-dropzone"
        />
        <Box sx={{ mt: 2 }}>
          <Table size="small" variant="ag-grid-standard">
            <TableHead>
              <TableRow>
                <TableCell>
                  Required input files{" "}
                  {stepError && touched && (
                    <Typography color="error" component="span">
                      * {stepError}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderFileStatus()}</TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

export default UploadConfigurationStep;
